import React from 'react';
import styled from '@emotion/styled';
import { useOnboarding } from '../../hooks/useOnboarding';
import OnboardingFlow from './OnboardingFlow';

const TriggerContainer = styled.div`
  text-align: center;
  padding: 48px 24px;
  background: linear-gradient(135deg, #fef7f7 0%, #fff0f3 100%);
  border-radius: 12px;
  border: 2px dashed #ffccd5;
  margin: 32px 0;
`;

const Icon = styled.div`
  font-size: 48px;
  margin-bottom: 24px;
  opacity: 0.7;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px;
`;

const Description = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px;
  line-height: 1.6;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #ff8a95, #ff6b85, #ff5577);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 133, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  text-decoration: underline;
  margin-left: 16px;
  font-size: 14px;

  &:hover {
    color: #374151;
  }
`;

interface OnboardingTriggerProps {
  icon?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  showSkip?: boolean;
  onSkip?: () => void;
}

const OnboardingTrigger: React.FC<OnboardingTriggerProps> = ({
  icon = '🚀',
  title = 'Welcome to Chala Flow!',
  description = 'Get started by setting up your account and choosing a plan. This will only take a few minutes.',
  buttonText = 'Get Started',
  showSkip = true,
  onSkip
}) => {
  const {
    needsOnboarding,
    showOnboarding,
    isCompleting,
    startOnboarding,
    skipOnboarding,
    completeOnboarding
  } = useOnboarding();

  const handleSkip = () => {
    skipOnboarding();
    onSkip?.();
  };

  if (!needsOnboarding) {
    return null;
  }

  return (
    <>
      <TriggerContainer>
        <Icon>{icon}</Icon>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <div>
          <Button
            onClick={startOnboarding}
            disabled={isCompleting}
          >
            {isCompleting ? 'Setting up...' : buttonText}
          </Button>
          {showSkip && (
            <SkipButton onClick={handleSkip}>
              Skip for now
            </SkipButton>
          )}
        </div>
      </TriggerContainer>

      {showOnboarding && (
        <OnboardingFlow
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
        />
      )}
    </>
  );
};

export default OnboardingTrigger;